import React, { useCallback, useState, useRef, useEffect } from 'react';
import { GoogleMap, InfoBox } from '@react-google-maps/api';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

import markerIcon from 'assets/img/pin.png';
import { GoogleMapsProps, MarkerData } from './GoogleMaps.types';
import { useGoogleMapsLoader } from './GoogleMapsLoader';

const disposeMarkerClusterer = (clusterer: MarkerClusterer | null): void => {
  if (!clusterer) {
    return;
  }
  clusterer.clearMarkers(true);
  clusterer.setMap(null);
};

const defaultProps: GoogleMapsProps = {
  zoom: 9,
  center: {
    lat: 51.509865,
    lng: -0.118092,
  },
  markerClusterGridSize: 50,
  markerData: [],
  onMarkerMouseOver: () => undefined,
  onMarkerMouseOut: () => undefined,
  infoWindowComponent: [],
  infoWindowPosition: null,
};

export const GoogleMapsWrapper = <P extends GoogleMapsProps>(WrappedComponent: React.ComponentType<P>) => {
  return (props: P): React.ReactElement => {
    const mergedProps = { ...defaultProps, ...props };

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...mergedProps} />;
  };
};

const GoogleMapsComponent = ({
  zoom,
  center,
  markerClusterGridSize,
  markerData,
  infoWindowComponent,
  infoWindowPosition,
  onMarkerMouseOver,
  onMarkerMouseOut,
}: GoogleMapsProps): React.ReactElement => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  const { isLoaded } = useGoogleMapsLoader();

  const googleMapOptions: google.maps.MapOptions = {
    clickableIcons: false,
    mapTypeControl: false,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels.icon',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    disposeMarkerClusterer(clustererRef.current);
    clustererRef.current = null;
    setMap(null);
  }, []);

  useEffect(() => {
    if (!map || !markerData || markerData.length === 0) {
      return undefined;
    }

    disposeMarkerClusterer(clustererRef.current);
    clustererRef.current = null;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const newMarkers = markerData.map((item: MarkerData) => {
      const marker = new google.maps.Marker({
        position: { lat: item.lat, lng: item.lng },
        icon: markerIcon,
        map: null,
      });

      marker.addListener('mouseover', (event: google.maps.MapMouseEvent) => {
        onMarkerMouseOver?.(item, event);
      });

      marker.addListener('mouseout', (event: google.maps.MapMouseEvent) => {
        onMarkerMouseOut?.(item, event);
      });

      return marker;
    });

    markersRef.current = newMarkers;

    clustererRef.current = new MarkerClusterer({
      map,
      markers: newMarkers,
      algorithm: new GridAlgorithm({ gridSize: markerClusterGridSize }),
    });

    return () => {
      disposeMarkerClusterer(clustererRef.current);
      clustererRef.current = null;
      markersRef.current.forEach((marker) => marker.setMap(null));
    };
  }, [map, markerData, markerClusterGridSize, onMarkerMouseOver, onMarkerMouseOut]);

  if (!isLoaded) {
    return <div style={{ height: '100%', width: '100%' }} />;
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        zoom={zoom}
        center={center}
        options={googleMapOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {infoWindowPosition && (
          <InfoBox
            position={
              new google.maps.LatLng(
                infoWindowPosition.lat ?? center?.lat ?? 0,
                infoWindowPosition.lng ?? center?.lng ?? 0,
              )
            }
            options={{
              closeBoxURL: '',
              enableEventPropagation: true,
              alignBottom: true,
              boxStyle: { overflow: 'initial', marginLeft: '16px' },
            }}
          >
            <div>{infoWindowComponent}</div>
          </InfoBox>
        )}
      </GoogleMap>
    </div>
  );
};

const GoogleMaps = GoogleMapsWrapper(GoogleMapsComponent);

export default GoogleMaps;
